/* eslint-disable no-unused-vars */
/**
 * @Description This is FasatModal component page.
 * This is a wrapper on "Modal" component from MUX without adding any custom functionlities
 * @FileName fasatModal.js
 * @Author SAMAPTI TALUKDAR-talusam
 * @CreatedOn 04 February, 2021 12:31:22
 * @IssueID CDTFASATMOD- 316
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import PubSub from 'pubsub-js';
import { Modal, uniqueId } from '@manulife/mux';
import './fasatModal.scss';
import FasatButton from '../fasatButton/fasatButton';
import { Logger } from '../../util';
import FasatToastContainer from '../fasatToast/fasatToastContainer';
import { checkDirtyData, releaseLockCombo, setLock } from '../../redux/actions/commonActions';

/**
*
* This should be a generic component which can be used for any kind of overlay
* This should have following feature:
* 1. Should be able to hide buttons
* 2. Should be able to disable primary button
* 3. Should be able to add children component
* 4. Should be able to raise an event for primary and secondary button clikc
* 5. Children should be able to handle those events
*/
/**
 * Controls modal visibility.
 * Note: MUX Modal handles scroll locking internally - do not manually set body overflow
 * as it can affect sidenav and other fixed/absolute positioned elements.
 */
const displayModal = (setModalOpen, display) => {
  setModalOpen(display);
};
const disableButton = (isDirty, SetDisableSave) => {
  if (isDirty) {
    SetDisableSave(false);
  } else {
    SetDisableSave(true);
  }
};
const secondaryButton = (onSecondaryButtonClick, secondaryTopic) => {
  onSecondaryButtonClick(secondaryTopic);
};

/**
 * Handles modal close and cleans up UI state.
 */
const modalCloseFunction = (setModalOpen, onModalClose, isPrimaryButtonClicked) => {
  setModalOpen(false);
  onModalClose(isPrimaryButtonClicked);
  const highlightedRow = document.querySelector('.highlightedRow');
  if (highlightedRow) {
    highlightedRow.classList.remove('highlightedRow');
  }
};

const onPrimaryClickFunction = (pt, modalClose) => {
  if (pt && pt !== '') {
    PubSub.publish(`${pt}`, { close: modalClose });
  }
};

const onSecondaryButtonClickFunction = (isDirty, modalClose, onCancelClickCancel, st, dispatch) => {
  if (isDirty) {
    onCancelClickCancel();
    PubSub.publish(`${st}`, { close: modalClose });
  } else if (!isDirty) {
    if (st && st !== '') {
      dispatch(checkDirtyData(false));
      PubSub.publish(`${st}`, { close: modalClose });
    }
  } else {
    Logger.verbose('error');
  }
};
const isDisabled = (isCheckDirty, DisableSave) => (isCheckDirty === 'yes' ? DisableSave : false);
const checkNotStayDiscard = (primaryButtonText, staydiscard) => (!!(primaryButtonText && !staydiscard));
const checkStayDiscardCon = (primaryButtonText, staydiscard) => (primaryButtonText && staydiscard);
const renderFasatToastContainer = (showNotificationInModal, toasterContainerId) => (
  showNotificationInModal && (
    <FasatToastContainer
      containerId={toasterContainerId}
      enableMultiContainer
    />
  ));
function RenderBtnCombo({ staydiscard, isLogout, secondaryTopic, onSecondaryButtonClick, secondaryButtonText,
  onClickDiscard, modalClose, secondaryBtnClickHandler }) {
  const handleSecondaryBtnClick = useCallback(() => {
    if (secondaryBtnClickHandler) {
      secondaryBtnClickHandler();
    } else { onSecondaryButtonClick(secondaryTopic); }
  }, [secondaryBtnClickHandler, onSecondaryButtonClick, secondaryTopic]);

  const handleonClickDiscard = useCallback(() => {
    onClickDiscard();
    modalClose();
  }, [onClickDiscard, modalClose]);
  return (
    <div className="left">
      {(!staydiscard || isLogout) && (
        <FasatButton
          variant="secondary"
          type="button"
          onClick={handleSecondaryBtnClick}
          id="modalSecondaryButton"
        >
          {secondaryButtonText}
        </FasatButton>
      )}
      {(staydiscard && !isLogout) && (
        <FasatButton
          variant="secondary"
          type="button"
          id="modalSecondaryButton"
          onClick={handleonClickDiscard}
        >
          {secondaryButtonText}
        </FasatButton>
      )}
    </div>
  );
}

function GetTertiaryBtn({ onClickTertiaryButton, tertiaryButtonText }) {
  const handleonClickTertiaryButton = useCallback(() => {
    onClickTertiaryButton();
  }, [onClickTertiaryButton]);
  return (
    <div className="left tertiaryBtn">
      <FasatButton
        variant="tertiary_back"
        type="button"
        id="modalTertiaryButton"
        onClick={handleonClickTertiaryButton}
      >
        {tertiaryButtonText}
      </FasatButton>
    </div>
  );
}
function GetThirdBtn({ onClickThirdBtn, modalClose, thirdButtonText }) {
  const handleOnClickThirdBtn = useCallback(() => {
    onClickThirdBtn();
    modalClose();
  }, [onClickThirdBtn, modalClose]);
  return (
    <div className="left">
      <FasatButton
        variant="secondary"
        type="button"
        id="modalThirdButton"
        onClick={handleOnClickThirdBtn}
      >
        {thirdButtonText}
      </FasatButton>
    </div>
  );
}
const renderModal = ({ modalOpen, staydiscard, modalClose, onSecondaryButtonClick, secondaryTopic,
  title, showCloseButton, modalWidth, modalClass, children, showActionButton, secondaryButtonText, thirdButtonText,
  isLogout, onClickDiscard, onClickThirdBtn, primaryButtonText, isCheckDirty, DisableSave, showNotificationInModal,
  toasterContainerId, id, thirdBtn, footerText, secondaryBtnClickHandler, tertiaryBtn, tertiaryButtonText,
  onClickTertiaryButton, onClickStayFunc, onCloseFunc, stayCloseFunc }) => {
  const unique = uniqueId();
  const headingId = `heading-${unique}`;
  return (
    <div>
      <Modal
        id={id}
        isOpen={modalOpen}
        onClose={onCloseFunc}
        aria-label={title}
        aria-labelledby={headingId}
        aria-describedby="content"
        shouldCloseOnBackdropClick={false}
        shouldCloseWithEscapeKey={false}
        showCloseButton={showCloseButton}
        /* Accessibility: maxWidth ensures modal shrinks at high zoom (up to 400%) per WCAG 2.1 SC 1.4.10 Reflow.
           16px margin preserved on sides to match MUX default behavior. */
        style={{ width: modalWidth, maxWidth: 'calc(100% - 16px)' }}
      >
        <div className={modalClass || ''}>
          <h2 id={headingId} className="modalHeading">{title}</h2>
          <div className="formContent">{children}</div>
          {showActionButton && (
          <div className="buttonContainer modalBtnContainer">
            {tertiaryBtn && <GetTertiaryBtn tertiaryBtn={tertiaryBtn} onClickTertiaryButton={onClickTertiaryButton} tertiaryButtonText={tertiaryButtonText} /> }
            {thirdBtn && <GetThirdBtn thirdBtn={thirdBtn} onClickThirdBtn={onClickThirdBtn} modalClose={modalClose} thirdButtonText={thirdButtonText} /> }
            {secondaryButtonText && (
            <RenderBtnCombo
              staydiscard={staydiscard}
              isLogout={isLogout}
              secondaryTopic={secondaryTopic}
              onSecondaryButtonClick={onSecondaryButtonClick}
              secondaryButtonText={secondaryButtonText}
              onClickDiscard={onClickDiscard}
              modalClose={modalClose}
              secondaryBtnClickHandler={secondaryBtnClickHandler}
            />
            )}
            {checkNotStayDiscard(primaryButtonText, staydiscard) && (
            <div className="right">
              <FasatButton
                type="submit"
                disabled={isDisabled(isCheckDirty, DisableSave)}
                onClick={onClickStayFunc}
                id="modalPrimaryButton"
              >
                {primaryButtonText}
              </FasatButton>
            </div>
            )}
            {checkStayDiscardCon(primaryButtonText, staydiscard) && (
            <div className="right">
              <FasatButton
                type="submit"
                id="modalPrimaryButton"
                disabled={isDisabled(isCheckDirty, DisableSave)}
                onClick={stayCloseFunc}
              >
                {primaryButtonText}
              </FasatButton>
            </div>
            )}
          </div>
          )}
          {footerText && <div className="left footer-text">{footerText}</div>}
        </div>
        {renderFasatToastContainer(showNotificationInModal, toasterContainerId)}
      </Modal>
    </div>
  );
};

function FasatModal({ title, secondaryButtonText, thirdButtonText, primaryButtonText, showActionButton,
  display, children, modalWidth, primaryTopic, secondaryTopic, modalClass, showCloseButton, defaultCheck,
  onModalClose, staydiscard, onCancelClickCancel, onClickDiscard, onClickThirdBtn, isCheckDirty, onClickStay, isLogout,
  showNotificationInModal, toasterContainerId, id, lockCombo, thirdBtn, tertiaryBtn, footerText, primaryBtnClickHandler,
  secondaryBtnClickHandler, tertiaryButtonText, onClickTertiaryButton,
}) {
  const [modalOpen, setModalOpen] = useState(display);
  const [disableSave, setDisableSave] = useState(true);
  const [comboLock, setComboLock] = useState(true);
  const isDirty = useSelector((state) => state.ApplicationState.isDirty);
  const loggedUser = useSelector(({ ApplicationStateRBAC }) => ApplicationStateRBAC?.loggedUser);
  const dispatch = useDispatch();
  useEffect(() => displayModal(setModalOpen, display), [display]);
  useEffect(() => {
    if (lockCombo) {
      setComboLock({ ...lockCombo, lockedBy: loggedUser.UserId });
      dispatch(setLock({ ...lockCombo, lockedBy: loggedUser.UserId }));
    }
  }, [lockCombo, loggedUser]);
  const modalClose = (isPrimaryButtonClicked) => {
    modalCloseFunction(setModalOpen, onModalClose, isPrimaryButtonClicked);
    if (lockCombo) {
      dispatch(releaseLockCombo(comboLock));
    }
  };
  useEffect(() => disableButton(isDirty, setDisableSave), [isDirty]);
  const processingInProgress = useSelector(({ ApplicationStateFilterList }) => ApplicationStateFilterList.processingInProgress);
  const onPrimaryButtonClick = (pt) => {
    if (!processingInProgress) {
      onPrimaryClickFunction(pt, modalClose);
    }
  };
  const onSecondaryButtonClick = (st) => {
    onSecondaryButtonClickFunction(isDirty, modalClose, onCancelClickCancel, st, dispatch);
  };
  const checkSecondaryHandler = () => {
    if (secondaryBtnClickHandler) {
      secondaryBtnClickHandler();
    } else {
      onSecondaryButtonClick(secondaryTopic);
    }
  };
  const stayCloseFunc = useCallback(() => {
    if (defaultCheck) {
      onPrimaryButtonClick(primaryTopic);
    } else {
      onClickStay();
      modalClose(true);
    }
  }, [onClickStay, modalClose, onPrimaryButtonClick, primaryTopic, defaultCheck]);
  const onClickStayFunc = useCallback(
    () => (primaryBtnClickHandler ? primaryBtnClickHandler() : onPrimaryButtonClick(primaryTopic)),
    [primaryBtnClickHandler, primaryBtnClickHandler, onPrimaryButtonClick, primaryTopic],
  );
  const onCloseFunc = useCallback(
    () => (staydiscard ? modalClose() : checkSecondaryHandler()),
    [modalClose, modalClose, checkSecondaryHandler],
  );
  return renderModal({
    modalOpen,
    staydiscard,
    modalClose,
    onCloseFunc,
    stayCloseFunc,
    onClickStayFunc,
    onSecondaryButtonClick,
    secondaryTopic,
    title,
    showCloseButton,
    modalWidth,
    modalClass,
    children,
    showActionButton,
    secondaryButtonText,
    thirdButtonText,
    tertiaryButtonText,
    isLogout,
    onClickDiscard,
    onClickThirdBtn,
    onClickTertiaryButton,
    primaryButtonText,
    isCheckDirty,
    onPrimaryButtonClick,
    primaryTopic,
    defaultCheck,
    onModalClose,
    onClickStay,
    showNotificationInModal,
    toasterContainerId,
    footerText,
    thirdBtn,
    tertiaryBtn,
    primaryBtnClickHandler,
    secondaryBtnClickHandler,
    id: id || `modal-id-${uniqueId()}`,
    DisableSave: disableSave,
  });
}

FasatModal.propTypes = {
  title: PropTypes.string,
  secondaryButtonText: PropTypes.string,
  thirdButtonText: PropTypes.string,
  tertiaryButtonText: PropTypes.string,
  primaryButtonText: PropTypes.string,
  showActionButton: PropTypes.bool,
  display: PropTypes.bool,
  children: PropTypes.shape(),
  modalWidth: PropTypes.string,
  primaryTopic: PropTypes.string,
  secondaryTopic: PropTypes.string,
  modalClass: PropTypes.string,
  showCloseButton: PropTypes.bool,
  onModalClose: PropTypes.func,
  onCancelClickCancel: PropTypes.func,
  onClickDiscard: PropTypes.func,
  onClickThirdBtn: PropTypes.func,
  onClickTertiaryButton: PropTypes.func,
  staydiscard: PropTypes.bool,
  isCheckDirty: PropTypes.string,
  onClickStay: PropTypes.func,
  isLogout: PropTypes.bool,
  defaultCheck: PropTypes.bool,
  showNotificationInModal: PropTypes.bool,
  toasterContainerId: PropTypes.string,
  id: PropTypes.string,
  lockCombo: PropTypes.string,
  thirdBtn: PropTypes.bool,
  tertiaryBtn: PropTypes.bool,
  footerText: PropTypes.string,
  primaryBtnClickHandler: PropTypes.func,
  secondaryBtnClickHandler: PropTypes.func,
};

FasatModal.defaultProps = {
  title: '',
  secondaryButtonText: '',
  thirdButtonText: '',
  tertiaryButtonText: '',
  primaryButtonText: '',
  showActionButton: true,
  display: false,
  children: {},
  modalWidth: '',
  primaryTopic: '',
  secondaryTopic: '',
  modalClass: '',
  showCloseButton: true,
  onModalClose: () => undefined,
  onClickStay: () => undefined,
  staydiscard: false,
  onCancelClickCancel: () => undefined,
  onClickDiscard: () => undefined,
  onClickThirdBtn: () => undefined,
  onClickTertiaryButton: () => undefined,
  isCheckDirty: '',
  isLogout: undefined,
  defaultCheck: false,
  showNotificationInModal: false,
  toasterContainerId: '',
  id: undefined,
  lockCombo: null,
  thirdBtn: false,
  tertiaryBtn: false,
  footerText: null,
};

export default FasatModal;
